import { Diskette, Stack as StackIcon } from '@good/icons';
import { Box, Button, Group, Stack, Divider, Collapse, Text, Paper, Center } from '@good/ui/core';
import _ from 'lodash';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IRootDispatch, IRootState } from 'stores/rematch/root-store';
import { CustomViewsModalType } from 'utilities/enum-utils';
import { CreateNewActivityRecordModalV2 } from '../components/listings/components/CreateNewActivityRecordModalV2';
import { useDisclosure } from '@good/ui/hooks';
import { BookingViewTabs } from './booking-view-tabs/booking-view-tabs';
import { PageContainer } from 'layouts/page-container';
import { ICustomView } from 'interfaces/custom-views-interface';
import CustomViewsModal from 'common-components/custom-views/modals/CustomViewsModal';
import { defaultFilterValue, BookingsViewType, BookingsViewLayoutType } from './types';
import { BookingsViewToolbarHeader } from './bookings-view-toolbar-header';
import { BookingsViewToolbarViewControl } from './bookings-view-toolbar-view-control';
import { BookingsViewToolbarRosterModeControl } from './bookings-view-toolbar-roster-mode-control';
import { BookingsViewToolbarFilters } from './booking-view-toolbar-filters';
import { useInitialiseTabs } from './use-initialise-tabs';
import { AddViewButton } from './add-view-button';
import { AllViewsButton } from './all-views-button';
import { LayoutTypeSegmentedControl } from './layout-type-segmented-control';
import { useBookings } from '../books-view-context';
import { PayPeriodAlert } from '../pay-period-alert';
import { BookingViewMenu } from './booking-view-tabs/booking-view-menu';
import { useBookingTabs } from '../books-view-context/booking-tab-view-context';
import { useChangeView } from '../books-view-context/use-change-view';

interface BookingsViewToolbarProps {
  view: BookingsViewType;
  onChangeView: (view: BookingsViewType) => void;
}

export const BookingsViewToolbar = ({ view, onChangeView }: BookingsViewToolbarProps) => {
  const bookingsFilter = useSelector((state: IRootState) => state.bookingsStore.bookingsFilter);
  const [createActivityRecordOpened, { open: openCreateActivityRecord, close: closeCreateActivityRecord }] =
    useDisclosure(false);
  const [layoutType, setLayoutType] = useState<BookingsViewLayoutType>('regular');
  const [bookingViewOpened, { open: openBookingView_, close: closeBookingView_ }] = useDisclosure(false);
  const [bookingViewType, setBookingViewType] = React.useState<CustomViewsModalType>(CustomViewsModalType.CREATE);
  const bookingListingActiveTab = useSelector((state: IRootState) => state.bookingsStore.bookingListingActiveTab);
  const dispatch = useDispatch<IRootDispatch>();
  const bookingViews = useSelector((state: IRootState) => state.bookingsStore.bookingViews);
  const defaultBookingViews = useSelector((state: IRootState) => state.bookingsStore.defaultBookingViews);
  const onOpenCreateBooking = useBookings((selector) => selector.onOpenCreateBooking);
  const [showBookingViewPopover, setShowBookingViewPopover] = useState(false);
  const removeViewFromSnapshot = useBookingTabs((selector) => selector.removeViewFromSnapshot);
  const changeView = useChangeView();
  useInitialiseTabs();

  const onOpenBookingViewModal = useCallback(
    (type: CustomViewsModalType) => {
      setBookingViewType(type);
      setShowBookingViewPopover(false);
      openBookingView_();
    },
    [openBookingView_],
  );

  const onChangeShowBookingViewPopover = useCallback((visible: boolean) => {
    setShowBookingViewPopover(visible);
  }, []);

  const onCloseBookingViewModal = useCallback(() => {
    setBookingViewType(CustomViewsModalType.CREATE);
    closeBookingView_();
  }, [closeBookingView_]);

  const onSaveView = useCallback(
    (tab: ICustomView) => {
      if (tab?.isOwner) {
        onOpenBookingViewModal(CustomViewsModalType.SAVE_VIEW);
      } else if (tab?.isDefault) {
        onOpenBookingViewModal(CustomViewsModalType.SAVE_DEFAULT_VIEW);
      } else {
        onOpenBookingViewModal(CustomViewsModalType.SAVE_AS_NEW_COPY_FROM_OTHERS);
      }
    },
    [onOpenBookingViewModal],
  );

  const onChangeLayoutType = useCallback((value: BookingsViewLayoutType) => {
    setLayoutType(value);
  }, []);

  const onAddView: typeof dispatch.bookingsStore.doAddBookingView = useCallback(
    async (payload) => {
      const { view, views } = await dispatch.bookingsStore.doAddBookingView(payload);
      changeView(view, views, { caller: 'BookingsViewToolbar.onAddView', takeSnapshot: true });
      return view;
    },
    [dispatch.bookingsStore.doAddBookingView, changeView],
  );

  const onUpdateView: typeof dispatch.bookingsStore.doUpdateBookingView = useCallback(
    async (payload) => {
      const { view, views } = await dispatch.bookingsStore.doUpdateBookingView(payload);
      changeView(view, views, { caller: 'BookingsViewToolbar.onUpdateView', takeSnapshot: true });
      return view;
    },
    [dispatch.bookingsStore.doUpdateBookingView, changeView],
  );

  const onDuplicateView: typeof dispatch.bookingsStore.doDuplicateBookingView = useCallback(
    async (payload) => {
      const { view, views } = await dispatch.bookingsStore.doDuplicateBookingView(payload);
      changeView(view, views, { caller: 'BookingsViewToolbar.onDuplicateView', takeSnapshot: true });
      return view;
    },
    [dispatch.bookingsStore.doDuplicateBookingView, changeView],
  );

  const onDeleteView: typeof dispatch.bookingsStore.doDeleteBookingView = useCallback(
    async (payload) => {
      const customViewId = await dispatch.bookingsStore.doDeleteBookingView(payload);
      removeViewFromSnapshot(customViewId);
      return customViewId;
    },
    [dispatch.bookingsStore.doDeleteBookingView, removeViewFromSnapshot],
  );

  return (
    <>
      <PageContainer pb={0}>
        <PayPeriodAlert mb='md' />
        <BookingsViewToolbarHeader
          openCreateBooking={onOpenCreateBooking}
          openCreateActivityRecord={openCreateActivityRecord}
        />
      </PageContainer>
      <Divider />
      <Box pos='sticky' top={60} style={{ zIndex: 1 }}>
        <PageContainer pt='sm' pb='sm' bg='rgb(251, 251, 251)'>
          <Group gap='xs'>
            <BookingsViewToolbarViewControl view={view} onChangeView={onChangeView} />
            <Divider orientation='vertical' my={4} />
            <LayoutTypeSegmentedControl value={layoutType} onChange={onChangeLayoutType} />
            {view === 'calendar' && (
              <>
                <Divider orientation='vertical' my={4} />
                <BookingsViewToolbarRosterModeControl />
              </>
            )}
            <Divider orientation='vertical' my={4} />
            <Button.Group>
              <Paper
                px='xl'
                withBorder
                bg='gray.0'
                style={{
                  borderRight: 'none',
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                }}
              >
                <Center h='100%'>
                  <StackIcon />
                  <Text size='sm' fw={600} ml='xs'>
                    Views
                  </Text>
                </Center>
              </Paper>
              {layoutType === 'compact' && <BookingViewMenu />}
              <Button
                onClick={() => onSaveView([bookingListingActiveTab])}
                fw={600}
                variant='default'
                leftSection={<Diskette />}
              >
                Save
              </Button>
              <AddViewButton
                openBookingViewModal={onOpenBookingViewModal}
                bookingViews={bookingViews}
                showPopover={showBookingViewPopover}
                onChangePopover={onChangeShowBookingViewPopover}
              />
              <AllViewsButton />
            </Button.Group>
          </Group>
        </PageContainer>
        <Divider />
        <Collapse in={layoutType === 'regular'}>
          <PageContainer pt='sm' pb={0} bg='rgb(253, 253, 253)'>
            <Stack gap='xs'>
              <BookingsViewToolbarFilters view={view} />
              <BookingViewTabs openBookingViewModal={onOpenBookingViewModal} />
            </Stack>
          </PageContainer>
        </Collapse>
        {layoutType === 'regular' && <Divider />}
      </Box>
      <CreateNewActivityRecordModalV2 isOpen={createActivityRecordOpened} onClose={closeCreateActivityRecord} />
      <CustomViewsModal
        defaultFilterValue={defaultFilterValue}
        defaultViews={defaultBookingViews}
        doAddView={onAddView}
        doDeleteView={onDeleteView}
        doDuplicateView={onDuplicateView}
        doUpdateViewTab={onUpdateView}
        isOpen={bookingViewOpened}
        onCloseViewModal={onCloseBookingViewModal}
        onUpdateViewModal={onOpenBookingViewModal}
        pageFilter={bookingsFilter}
        pageListingActiveTab={bookingListingActiveTab}
        pageViews={bookingViews}
        setPageFilter={dispatch.bookingsStore.setBookingsFilter}
        type={bookingViewType}
      />
    </>
  );
};
